import React, { useCallback, useEffect, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { InteractiveQuestionPropType, VariantPropType } from 'propTypes/common';

import Button from 'components/Button';
import BaseSlider from 'components/sliders/BaseSlider';

import styles from './styles.module.scss';

const nextQuestionButtonTextHash = {
	question: 'Следующий вопрос',
	result: 'Показать результат',
	select: 'Ответить'
};

const QuestionsSlider = ({
	type,
	questions = [],
	renderQuestion,
	selectedVariants,
	finishTest,
	contentContainerRef
}) => {
	const [sliderActiveIndex, setSliderActiveIndex] = useState(
		selectedVariants.filter((variant) => !_.isEmpty(variant)).length
	);
	const [swiper, setSwiper] = useState(null);
	const isLastQuestion = questions.length - 1 === sliderActiveIndex;
	const [submittedQuestions, setSubmittedQuestions] = useState({});

	const getSwiper = (swiperInstance) => {
		setSwiper(swiperInstance);
	};

	const sliderParams = {
		slidesPerView: 1,
		spaceBetween: 0,
		onActiveIndexChange: ({ activeIndex }) => {
			setSliderActiveIndex(activeIndex);
		},
		noSwiping: true,
		allowTouchMove: false,
		autoHeight: true,
		observer: true,
		observeParents: true,
		observeSlideChildren: true,
		resizeObserver: true
	};

	useEffect(() => {
		if (
			swiper &&
			sliderActiveIndex !== 0 &&
			!selectedVariants.find((variant) => !_(variant).isEmpty())
		) {
			setSliderActiveIndex(0);
			setSubmittedQuestions({});
			try {
				swiper?.slideTo(0);
				swiper?.update();
				// eslint-disable-next-line no-empty
			} catch {}
		}
	}, [selectedVariants]);

	const getQuestionHashKey = () => {
		const currentVariant = selectedVariants[sliderActiveIndex];
		const isCurrentQuestionAnswered = !!submittedQuestions[sliderActiveIndex];
		switch (type) {
			case 'testTree':
				return currentVariant?.nextType || 'question';
			case 'testCorrect':
				if (isCurrentQuestionAnswered && isLastQuestion) {
					return 'result';
				}
				return isCurrentQuestionAnswered ? 'question' : 'select';

			case 'testSummary':
				return isLastQuestion ? 'result' : 'question';
			default:
				return null;
		}
	};

	const renderNextButton = useCallback(() => {
		const currentVariant = selectedVariants[sliderActiveIndex];
		const currentQuestion = questions[sliderActiveIndex];

		const nextQuestionKey = getQuestionHashKey();
		const isDisabled =
			currentQuestion?.mode === 'matchings'
				? !currentVariant?.isFilled
				: _.isEmpty(selectedVariants[sliderActiveIndex]);

		return (
			<Button
				title={nextQuestionButtonTextHash[nextQuestionKey]}
				className={cx(styles.SliderNextButton, {
					[styles.SliderNextButton_disabled]: isDisabled
				})}
				disabled={isDisabled}
				onClick={() => {
					switch (nextQuestionKey) {
						case 'select':
							setSubmittedQuestions((prevValue) => ({
								...prevValue,
								[sliderActiveIndex]: true
							}));
							contentContainerRef.current.scrollIntoView({
								behavior: 'smooth',
								block: 'start'
							});
							break;
						case 'question': {
							swiper.slideTo(
								type === 'testTree'
									? currentVariant.nextIndex
									: sliderActiveIndex + 1,
								200,
								false
							);

							const observerSwiper = new MutationObserver((mutations) => {
								mutations.forEach(() => {
									contentContainerRef.current.scrollIntoView({
										behavior: 'smooth',
										block: 'start'
									});
								});

								observerSwiper.disconnect();
							});

							observerSwiper.observe(
								swiper.$el.children('.swiper-wrapper')[0],
								{ attributes: true, attributeFilter: ['style'] }
							);
							break;
						}
						case 'result':
							contentContainerRef.current.scrollIntoView({
								behavior: 'smooth',
								block: 'start'
							});
							finishTest();
							break;
						default:
							break;
					}
				}}
			/>
		);
	}, [selectedVariants, sliderActiveIndex, submittedQuestions]);

	return (
		<div>
			<BaseSlider
				params={sliderParams}
				slideHeight="fill"
				darkColorTheme
				leftButtonClassName={styles.SliderPrevButton}
				rightButtonClassName={styles.SliderPrevButton}
				getSwiper={getSwiper}
				renderNextButton={renderNextButton}
				alwaysShowNavigation
			>
				{questions.map((question, index) =>
					renderQuestion(
						question,
						index,
						!!submittedQuestions[sliderActiveIndex]
					)
				)}
			</BaseSlider>
		</div>
	);
};

QuestionsSlider.propTypes = {
	type: PropTypes.string.isRequired,
	questions: PropTypes.arrayOf(InteractiveQuestionPropType),
	renderQuestion: PropTypes.func,
	selectedVariants: PropTypes.arrayOf(
		PropTypes.oneOfType([VariantPropType, PropTypes.object])
	),
	finishTest: PropTypes.func,
	contentContainerRef: PropTypes.object
};

export default QuestionsSlider;
